import { QuiltedHealthCodeSystems } from "@/fhir/enums/quilted-health";
import { Systems } from "@/fhir/enums/systems";
import { PractitionerPermissionFlags } from "@/permission/constants/practitioner-permissions";
import { joinTokenString } from "@/utils/medplum";

export enum AOEStatus {
  None = "none",
  Loading = "loading",
  Loaded = "loaded",
  Error = "error",
}

export enum Priority {
  Routine = "routine",
  STAT = "stat",
}

export enum BillingOptions {
  Patient = "patient",
  Insurance = "insurance",
  CustomerAccount = "customer-account",
}

export enum HealthGorillaCodes {
  ClinicalPDFReport = "ClinicalPDFReport1",
}

export const BILLING_OPTIONS = {
  [BillingOptions.Patient]: "Client",
  [BillingOptions.Insurance]: "Insurance",
  [BillingOptions.CustomerAccount]: "Practice",
} as const;

export enum HealthGorillaLabOrderInternalStatus {
  All = "all",
  NeedsApproval = "needs-approval",
  Ordered = "ordered",
  NeedsReview = "needs-review",
  Reviewed = "reviewed",
  Denied = "denied",
  Cancelled = "cancelled",
}

export enum LabOrderResultsSortKeys {
  OrderedDate = "authored",
  ReceivedDate = "occurrence",
}

/** Newest ordered date first — the sort applied within each status group of the practice dashboard. */
export const LAB_ORDER_RESULTS_ORDERED_DATE_SORT = `-${LabOrderResultsSortKeys.OrderedDate}`;

export interface HealthGorillaRequiredFields {
  hasFullName: boolean;
  hasBirthDate: boolean;
  hasLegalSex: boolean;
  hasSexAssignedAtBirth: boolean;
  hasLanguages: boolean;
  hasPhoneNumber: boolean;
  hasAddress: boolean;
}

export const HealthGorillaLabOrderInternalStatusDisplay = {
  [HealthGorillaLabOrderInternalStatus.All]: "All",
  [HealthGorillaLabOrderInternalStatus.NeedsApproval]: "Needs Approval",
  [HealthGorillaLabOrderInternalStatus.Ordered]: "Ordered",
  [HealthGorillaLabOrderInternalStatus.NeedsReview]: "Needs Review",
  [HealthGorillaLabOrderInternalStatus.Reviewed]: "Reviewed",
  [HealthGorillaLabOrderInternalStatus.Denied]: "Denied",
  [HealthGorillaLabOrderInternalStatus.Cancelled]: "Cancelled",
} as const;

export const HealthGorillaLabOrderInternalStatusFilterDisplay = {
  [HealthGorillaLabOrderInternalStatus.All]: "All",
  [HealthGorillaLabOrderInternalStatus.NeedsApproval]: "Needs Approval",
  [HealthGorillaLabOrderInternalStatus.Ordered]: "Ordered",
  [HealthGorillaLabOrderInternalStatus.NeedsReview]: "Needs Review",
  [HealthGorillaLabOrderInternalStatus.Reviewed]: "Reviewed / Closed",
  [HealthGorillaLabOrderInternalStatus.Denied]: "Reviewed / Closed",
  [HealthGorillaLabOrderInternalStatus.Cancelled]: "Reviewed / Closed",
} as const;

export const VALID_SEARCH_PARAMS_FILTERS = ["status"];

const REVIEWED_AND_CLOSED_STATUS_LIST = [
  HealthGorillaLabOrderInternalStatus.Reviewed,
  HealthGorillaLabOrderInternalStatus.Cancelled,
  HealthGorillaLabOrderInternalStatus.Denied,
] as const;

export const REVIEWED_AND_CLOSED_STATUSES = REVIEWED_AND_CLOSED_STATUS_LIST.join(",");

/**
 * Status groups the practice dashboard lists in, most actionable first.
 *
 * Every lab order carries one of these statuses, so the groups together cover the whole list and
 * the default view still holds every lab order; the order only decides what a user reads first.
 */
export const LAB_ORDER_STATUS_SORT_GROUPS: readonly (readonly HealthGorillaLabOrderInternalStatus[])[] =
  [
    [HealthGorillaLabOrderInternalStatus.NeedsApproval],
    [HealthGorillaLabOrderInternalStatus.NeedsReview],
    [HealthGorillaLabOrderInternalStatus.Ordered],
    REVIEWED_AND_CLOSED_STATUS_LIST,
  ];

export const TERMINAL_LAB_ORDER_INTERNAL_STATUSES = [
  HealthGorillaLabOrderInternalStatus.Cancelled,
  HealthGorillaLabOrderInternalStatus.Denied,
] as const;

export const HEALTH_GORILLA_OBSERVATION_INTERPRETATION_SYSTEMS = [
  Systems.HL7InterpretationCodes,
  Systems.HL7ObservationInterpretation,
  Systems.HealthGorillaObservationInterpretation,
] as const;

export const HealthGorillaNotifiableStatuses = [
  HealthGorillaLabOrderInternalStatus.NeedsApproval,
  HealthGorillaLabOrderInternalStatus.NeedsReview,
];

export const LAB_ORDER_RESULTS_NOTE_CODE = "forms/lab-order-results/note";

/** Account numbers available to a practice, keyed by performing lab reference string. */
export interface PerformingLabAccounts {
  /** The practice's account number with each performing lab. */
  accountNumbersByLab: Record<string, string>;
  /** The ordering physician's account number with each performing lab, where one is configured. */
  physicianAccountNumbersByLab: Record<string, string>;
}

export const ALLOWED_REQUESTOR_PERMISSIONS = [
  joinTokenString(
    QuiltedHealthCodeSystems.PractitionerPermission,
    PractitionerPermissionFlags.ProviderRole,
  )!,
  joinTokenString(
    QuiltedHealthCodeSystems.PractitionerPermission,
    PractitionerPermissionFlags.AdminProvider,
  )!,
].join(",");

/** What an uploaded result is. Both kinds share the lab results list. */
export enum UploadedResultType {
  Lab = "lab",
  Imaging = "imaging",
}

export type AppendEpisodeOfCareReferralFailureReason =
  | "missing_service_request_id"
  | "missing_episode_of_care_id"
  | "missing_version_id"
  | "missing_reference"
  | "patch_failed";

export type AppendEpisodeOfCareReferralResult =
  | { ok: true; alreadyLinked?: boolean }
  | { ok: false; reason: AppendEpisodeOfCareReferralFailureReason; details?: unknown };
